export type GeographyCountry = {
  id: number
  iso2: string
  name_en: string
  name_ar: string
}

export type GeographyState = {
  id: number
  country_id: number
  code: string
  name_en: string
  name_ar: string
}

export type GeographyCity = {
  id: number
  state_id: number
  name_en: string
  name_ar: string
}

type RawLocationData = {
  countries: { code: string, name: string }[]
  states: { countryCode: string, code: string, name: string }[]
  cities: Record<string, Record<string, string[]>>
}

const COUNTRY_AR: Record<string, string> = {
  EG: 'مصر',
  SA: 'السعودية',
  AE: 'الإمارات العربية المتحدة',
  JO: 'الأردن',
  LB: 'لبنان',
  SY: 'سوريا',
  IQ: 'العراق',
  KW: 'الكويت',
  QA: 'قطر',
  BH: 'البحرين',
  OM: 'عُمان',
  YE: 'اليمن',
  PS: 'فلسطين',
  LY: 'ليبيا',
  TN: 'تونس',
  DZ: 'الجزائر',
  MA: 'المغرب',
  SD: 'السودان',
  US: 'الولايات المتحدة',
  GB: 'المملكة المتحدة',
  FR: 'فرنسا',
  DE: 'ألمانيا',
  TR: 'تركيا',
}

/** Countries shown in location pickers (MENA / Middle East focus). */
export const MIDDLE_EAST_COUNTRY_ISO_CODES = [
  'EG',
  'SA',
  'AE',
  'JO',
  'LB',
  'SY',
  'IQ',
  'KW',
  'QA',
  'BH',
  'OM',
  'YE',
  'PS',
  'LY',
  'TN',
  'DZ',
  'MA',
  'SD',
  'TR',
] as const

const middleEastCountryIsoSet = new Set<string>(MIDDLE_EAST_COUNTRY_ISO_CODES)

const middleEastCountryOrder = new Map<string, number>(
  MIDDLE_EAST_COUNTRY_ISO_CODES.map((iso, index) => [iso, index]),
)

function filterSelectableCountries(list: GeographyCountry[]): GeographyCountry[] {
  return list
    .filter(c => middleEastCountryIsoSet.has(c.iso2))
    .sort(
      (a, b) =>
        (middleEastCountryOrder.get(a.iso2) ?? 999)
        - (middleEastCountryOrder.get(b.iso2) ?? 999),
    )
}

const STATE_AR: Record<string, Record<string, string>> = {
  EG: {
    Cairo: 'القاهرة',
    Giza: 'الجيزة',
    Alexandria: 'الإسكندرية',
    Dakahlia: 'الدقهلية',
    Sharqia: 'الشرقية',
    Asyut: 'أسيوط',
    'Red Sea': 'البحر الأحمر',
    Beheira: 'البحيرة',
    'Beni Suef': 'بني سويف',
    Damietta: 'دمياط',
    Faiyum: 'الفيوم',
    Gharbia: 'الغربية',
    Ismailia: 'الإسماعيلية',
    'Kafr el-Sheikh': 'كفر الشيخ',
    Luxor: 'الأقصر',
    Matrouh: 'مطروح',
    Minya: 'المنيا',
    Monufia: 'المنوفية',
    'New Valley': 'الوادي الجديد',
    'North Sinai': 'شمال سيناء',
    'Port Said': 'بورسعيد',
    Qalyubia: 'القليوبية',
    Qena: 'قنا',
    'South Sinai': 'جنوب سيناء',
    Sohag: 'سوهاج',
    Suez: 'السويس',
    Aswan: 'أسوان',
  },
}

import egCitiesAr from './eg-cities-ar.json'

const CITIES_PER_STATE = 10_000

let raw: RawLocationData | null = null
let countries: GeographyCountry[] | null = null
let states: GeographyState[] | null = null
let countryIdByIso: Map<string, number> | null = null
let countryIsoById: Map<number, string> | null = null
const citiesByStateId = new Map<number, GeographyCity[]>()
let loadPromise: Promise<void> | null = null

function cityIdFor(stateId: number, index: number): number {
  return stateId * CITIES_PER_STATE + index + 1
}

function cityLabel(countryIso: string, name: string): string {
  if (countryIso === 'EG') return egCitiesAr[name as keyof typeof egCitiesAr] ?? name
  return name
}

function buildCountriesAndStates(data: RawLocationData) {
  raw = data

  countries = data.countries.map((c, index) => ({
    id: index + 1,
    iso2: c.code,
    name_en: c.name,
    name_ar: COUNTRY_AR[c.code] ?? c.name,
  }))

  countryIdByIso = new Map(countries.map(c => [c.iso2, c.id]))
  countryIsoById = new Map(countries.map(c => [c.id, c.iso2]))

  states = []
  let stateId = 1

  for (const s of data.states) {
    const country_id = countryIdByIso.get(s.countryCode)
    if (!country_id) continue

    states.push({
      id: stateId++,
      country_id,
      code: s.code,
      name_en: s.name,
      name_ar: STATE_AR[s.countryCode]?.[s.name] ?? s.name,
    })
  }
}

function buildCitiesForState(stateId: number): GeographyCity[] {
  if (!raw || !states || !countryIsoById) return []

  const state = states.find(s => s.id === stateId)
  if (!state) return []

  const countryIso = countryIsoById.get(state.country_id)
  if (!countryIso) return []

  const names = raw.cities[countryIso]?.[state.code] ?? []
  return names.map((name, index) => ({
    id: cityIdFor(stateId, index),
    state_id: stateId,
    name_en: name,
    name_ar: cityLabel(countryIso, name),
  }))
}

async function ensureLoaded(): Promise<void> {
  if (countries) return
  if (!loadPromise) {
    loadPromise = import('./location-data.json').then((mod) => {
      buildCountriesAndStates((mod.default ?? mod) as RawLocationData)
    })
  }
  await loadPromise
}

export async function getCountries(): Promise<GeographyCountry[]> {
  await ensureLoaded()
  return filterSelectableCountries(countries!)
}

export async function getStates(countryId: number): Promise<GeographyState[]> {
  await ensureLoaded()
  const id = Number(countryId)
  return states!.filter(s => s.country_id === id)
}

export async function getCities(stateId: number): Promise<GeographyCity[]> {
  await ensureLoaded()
  if (!citiesByStateId.has(stateId)) {
    citiesByStateId.set(stateId, buildCitiesForState(stateId))
  }
  return citiesByStateId.get(stateId)!
}

export async function findCountry(id: number | null | undefined): Promise<GeographyCountry | undefined> {
  if (id == null) return undefined
  await ensureLoaded()
  return countries!.find(c => c.id === id)
}

export async function findState(id: number | null | undefined): Promise<GeographyState | undefined> {
  if (id == null) return undefined
  await ensureLoaded()
  return states!.find(s => s.id === id)
}

export async function findCity(id: number | null | undefined): Promise<GeographyCity | undefined> {
  if (id == null) return undefined
  await ensureLoaded()
  const stateId = Math.floor((id - 1) / CITIES_PER_STATE)
  if (stateId < 1) return undefined
  const list = await getCities(stateId)
  return list.find(c => c.id === id)
}

export const DEFAULT_COUNTRY_ISO = 'EG'

export async function getDefaultCountryId(): Promise<number | null> {
  const country = await findCountryByIso(DEFAULT_COUNTRY_ISO)
  return country?.id ?? null
}

export async function findCountryByIso(iso2: string): Promise<GeographyCountry | undefined> {
  await ensureLoaded()
  const id = countryIdByIso!.get(iso2)
  return id ? countries!.find(c => c.id === id) : undefined
}
